import { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material';
import TableLoadingState from './TableLoadingState';
import TableEmptyState from './TableEmptyState';
import TreeTableRow from './TreeTableRow';

const TreeTable = ({
  data = [],
  columns = [],
  renderActions,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  renderToolbar,
}) => {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const flattenTree = useCallback(
    (nodes, depth = 0, parentVisible = true) => {
      const result = [];
      for (const node of nodes) {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);
        const isVisible = parentVisible;

        result.push({ node, depth, hasChildren, isExpanded, isVisible });

        if (hasChildren) {
          result.push(
            ...flattenTree(node.children, depth + 1, isVisible && isExpanded),
          );
        }
      }
      return result;
    },
    [expandedIds],
  );

  const allRows = flattenTree(data);

  return (
    <Box>
      {renderToolbar && <Box sx={{ mb: 2 }}>{renderToolbar()}</Box>}

      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align || 'left'}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && data.length === 0 && (
              <TableLoadingState colSpan={columns.length} />
            )}

            {!loading && data.length === 0 && (
              <TableEmptyState
                colSpan={columns.length}
                message={emptyMessage}
              />
            )}

            {allRows.map((row) => (
              <TreeTableRow
                key={row.node.id}
                {...row}
                columns={columns}
                onRowClick={onRowClick}
                onToggleExpand={toggleExpand}
                renderActions={renderActions}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TreeTable;
